import { Actions, createEffect, ofType } from '@ngrx/effects';
import { withLatestFrom, mergeMap, tap, map, catchError } from 'rxjs/operators';
import {
    paymentActions,
    queuePaymentDetailsRequest,
    updatePaymentDetailsResponse,
} from 'stores/payment-details/payment.actions';
import { selectPaymentDetails } from 'stores/payment-details/payment.selectors';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { PaymentService } from 'services/api/payment.service';
import { RequestQueuingService } from 'shared/base/generics/globals/request-queue/request-queuing.service';
import merge from 'lodash/merge';
import { RequestQueueActions } from 'shared/base/generics/globals/request-queue/request-queue.module';
import { of } from 'rxjs/internal/observable/of';
import { requestFailure } from 'sharedStores/error/error.actions';
import { QuoteControllerSelectors } from 'shared/base/generics/store/quote-controller/quote-controller.module';
import { setDropOffState } from 'shared/base/generics/store/drop-off/drop-off.actions';

@Injectable({ providedIn: 'any' })
export class PaymentEffects {
    paymentDetails = createEffect(
        () =>
            this.actions$.pipe(
                ofType(...(Object.values(paymentActions) as any)),
                withLatestFrom(
                    this.store$.select(selectPaymentDetails),
                    this.store$.select(QuoteControllerSelectors.selectActivePage),
                ),
                tap(([request, latest, activePage]) => {
                    let paymentRequest = latest.paymentDetails;
                    if (request.paymentDetails) {
                        paymentRequest = merge(paymentRequest, request.paymentDetails);
                    }
                    this.queuingService$.dispatch(
                        queuePaymentDetailsRequest({
                            paymentDetails: paymentRequest,
                            traceId: request.type,
                            accountDetailsPage: request.accountDetailsPage,
                            doAvsCheck: request.doAvsCheck,
                            doBankConsentCall: request.doBankConsentCall,
                            isDropOffAnchorPoint: activePage?.page?.isDropOffAnchorPoint ?? false,
                        }),
                    );
                }),
            ),
        {
            dispatch: false,
        },
    );

    addUpdatePaymentDetails = createEffect(
        () =>
            this.actions$.pipe(
                ofType(queuePaymentDetailsRequest),
                withLatestFrom(this.store$.select(selectPaymentDetails)),
                mergeMap(([request]) => {
                    return this.paymentService$
                        .addUpdatePayment(
                            request.paymentDetails,
                            request.doAvsCheck,
                            request.doBankConsentCall,
                            request.isDropOffAnchorPoint,
                        )
                        .pipe(
                            map((response) => {
                                const code = response?.dropOffState?.code;
                                if (code) {
                                    this.store$.dispatch(
                                        setDropOffState({ dropOffState: { code } }),
                                    );
                                }
                                return updatePaymentDetailsResponse({
                                    avsAttempts: response.avsAttempts,
                                    paymentDetails: response.paymentDetails,
                                    accountDetailsPage: request.accountDetailsPage,
                                    paymentDetailsHistory: response.paymentDetailsHistory,
                                });
                            }),
                            catchError((error) => of(requestFailure(error))),
                        );
                }),
                mergeMap((responseAction) => [
                    responseAction,
                    RequestQueueActions.requestComplete({ traceId: responseAction.action }),
                ]),
            ),
        {
            dispatch: true,
        },
    );

    constructor(
        private readonly actions$: Actions,
        private readonly paymentService$: PaymentService,
        private readonly store$: Store,
        private readonly queuingService$: RequestQueuingService,
    ) {}
}
